// Прикрепление файлов: кнопка «скрепка» (выбор файла) + кнопка «камера»
// (скриншот экрана через getDisplayMedia). До 3 файлов на сообщение.
//
// Для гостей captchaToken обязателен — иначе upload вернёт 400.
import React, { useState } from "react";
import { uploadAttachment, attachmentUrl } from "../../api/tickets";
import { PaperclipIcon } from "../icons/PaperclipIcon";
import { CameraIcon } from "../icons/CameraIcon";

const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Props:
 *   attachments: [{file_id, name, mime, preview_url?}]
 *   onChange(next: list) — список обновлён
 *   captchaToken?       — нужен гостям
 */
export default function TicketAttachmentUpload({
  attachments,
  onChange,
  captchaToken,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const add = async (file) => {
    setErr(null);
    if (!file) return;
    if (attachments.length >= MAX_FILES) {
      setErr(`Максимум ${MAX_FILES} файла на сообщение.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr("Файл больше 5 МБ.");
      return;
    }
    setBusy(true);
    try {
      const res = await uploadAttachment(file, { captchaToken });
      onChange([
        ...attachments,
        {
          file_id: res.file_id,
          name: file.name,
          mime: file.type,
          preview_url: file.type.startsWith("image/")
            ? attachmentUrl(res.file_id)
            : null,
        },
      ]);
    } catch (e) {
      setErr(e.message || "Не удалось загрузить файл.");
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    add(f);
  };

  const onScreenshot = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      setErr("Браузер не поддерживает захват экрана.");
      return;
    }
    setErr(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      const track = stream.getVideoTracks()[0];
      // Один кадр — сразу останавливаем поток.
      const bitmap = await new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.srcObject = stream;
        video.onloadedmetadata = () => video.play();
        video.onplaying = () => {
          const c = document.createElement("canvas");
          c.width = video.videoWidth;
          c.height = video.videoHeight;
          c.getContext("2d").drawImage(video, 0, 0);
          track.stop();
          c.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("blob_failed"));
          }, "image/png");
        };
        video.onerror = () => reject(new Error("video_error"));
      });
      const file = new File(
        [bitmap],
        `screenshot-${Date.now()}.png`,
        { type: "image/png" }
      );
      add(file);
    } catch (e) {
      // Пользователь отменил или браузер отказал — это норма.
      if (e && e.name !== "NotAllowedError" && e.name !== "AbortError") {
        setErr("Скриншот не удалось сделать.");
      }
    }
  };

  const removeAt = (idx) => {
    const next = attachments.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div>
      <div className="sup-attach-row">
        {attachments.map((a, idx) => (
          <div key={a.file_id} className="sup-attach-thumb" title={a.name}>
            {a.preview_url ? (
              <img src={a.preview_url} alt={a.name} />
            ) : (
              <span>{a.name.length > 8 ? a.name.slice(0, 6) + "…" : a.name}</span>
            )}
            <button
              type="button"
              className="sup-attach-thumb__del"
              onClick={() => removeAt(idx)}
              aria-label="Удалить"
              title="Удалить"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="sup-attach-btns" style={{ marginTop: 8 }}>
        <label className="sup-btn-ghost" style={{ cursor: "pointer" }}>
          <PaperclipIcon size={16} />
          {busy ? "Загрузка…" : "Прикрепить файл"}
          <input
            type="file"
            accept="image/*,video/mp4"
            style={{ display: "none" }}
            disabled={busy}
            onChange={onPickFile}
          />
        </label>
        <button
          type="button"
          className="sup-btn-ghost"
          onClick={onScreenshot}
          disabled={busy}
        >
          <CameraIcon size={16} />
          Скриншот экрана
        </button>
      </div>
      <div className="sup-form__hint">
        До {MAX_FILES} файлов, по 5 МБ. PNG/JPG/GIF/WEBP/MP4.
      </div>
      {err && <div className="sup-msg-err" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
}
